import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, MessageCircle, Paperclip, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Picker, Templates } from "./WaMessagesPanel";
import { api } from "../lib/api";

const minLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

// Forward a correspondence message (text + optional file) to one or more employees via WhatsApp
export const WaForwardDialog = ({ open, onOpenChange, text = "", attachments = [], defaultIds = [] }) => {
  const [emps, setEmps] = useState([]);
  const [ids, setIds] = useState([]);
  const [body, setBody] = useState("");
  const [att, setAtt] = useState("");
  const [later, setLater] = useState(false);
  const [at, setAt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBody(text); setIds(defaultIds); setAtt(attachments[0]?.id || ""); setLater(false); setAt("");
    api.listEmployees().then((d) => setEmps((Array.isArray(d) ? d : d.items || d.employees || []).filter((e) => e.status !== "terminated"))).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    const payload = { employee_ids: ids, body, ref_type: "correspondence", attachment_id: att };
    setBusy(true);
    try {
      if (later) {
        if (!at) { toast.error("حدّد وقت الإرسال"); return; }
        await api.waSchedule({ ...payload, send_at: new Date(at).toISOString() });
        toast.success(`جُدولت الرسالة إلى ${ids.length} موظف`); onOpenChange(false); return;
      }
      const r = await api.waSend(payload);
      if (r.sent) { toast.success(`أُرسلت ${r.sent} من ${r.total}`); onOpenChange(false); }
      else toast.error(r.no_phone_count ? "لا يوجد رقم جوال مسجّل للموظفين المختارين" : `لم تُرسل: ${r.failed?.[0]?.reason || "خطأ"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="w-[95vw] max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="wa-forward-dialog">
        <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><MessageCircle className="h-5 w-5 text-emerald-600" /> إرسال عبر واتساب</DialogTitle>
          <DialogDescription className="text-start">تُرسل من رقم واتساب الشركة إلى جوالات الموظفين المختارين.</DialogDescription></DialogHeader>
        <Picker employees={emps} ids={ids} setIds={setIds} />
        <Templates body={body} setBody={setBody} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="w-full rounded-lg border border-slate-200 p-3 text-sm" placeholder="نص الرسالة… يمكن استخدام {الاسم}" data-testid="wa-forward-text" />
        {attachments.length > 0 && (
          <div className="space-y-1 rounded-lg bg-slate-50 p-2.5" data-testid="wa-forward-attachments">
            <p className="flex items-center gap-1 text-xs font-semibold text-slate-600"><Paperclip className="h-3.5 w-3.5" /> إرفاق ملف (يُرسل الملف نفسه وليس رابطاً)</p>
            {[{ id: "", name: "بدون مرفق" }, ...attachments].map((a) => (
              <label key={a.id || "none"} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="wa-att" checked={att === a.id} onChange={() => setAtt(a.id)} data-testid={`wa-forward-att-${a.id || "none"}`} />
                <span className="truncate">{a.name}</span></label>))}
          </div>)}
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 p-2.5">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={later} onCheckedChange={setLater} data-testid="wa-forward-schedule-switch" /><CalendarClock className="h-4 w-4 text-indigo-600" /> جدولة الإرسال</label>
          {later && <input type="datetime-local" value={at} min={minLocal()} onChange={(e) => setAt(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="wa-forward-schedule-at" />}
        </div>
        <Button onClick={send} disabled={busy || !ids.length || (!body.trim() && !att)} className={`w-full gap-2 ${later ? "bg-indigo-600 hover:bg-indigo-700" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="wa-forward-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : later ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />} {later ? "جدولة" : "إرسال"} إلى {ids.length} موظف
        </Button>
      </DialogContent>
    </Dialog>
  );
};
